/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {AuditChange, AuditRecord, GenericObject} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {AuditTransformOptions, getJSON} from '@ion/dls/src/editors/audit/AuditGridUtils';

/**
 * Builds an audit change from a scout audit record entity, used for
 * create records where the audit service omits `changes`.
 *
 * ```typescript
 *   import {getScoutEntityChanges} from './ScoutAuditUtils';
 *
 *   const changes = getScoutEntityChanges(record.entity);
 *   // [{fieldName: 'mqttInitCommand', oldValue: '{"operation":...}', ...}] ...
 *
 * ```
 */
export const getScoutEntityChanges = (entity: any): AuditChange[] => {
  const {operationType, ...details} = getJSON(entity)?.vescout || {};
  return operationType && details[operationType] ? [{
    fieldName : operationType,
    links     : [],
    oldValue  : safeJSONStringify(details[operationType]),
    newValue  : ''
  }] : [];
};

/**
 * Fills the `changes` of the given scout audit records that omit them (e.g.
 * create records) with the change built from their entity.
 *
 * ```typescript
 *   import {withScoutEntityChanges} from './ScoutAuditUtils';
 *
 *   const records = withScoutEntityChanges(results);
 *   // [{action: 'CREATE', changes: [{fieldName: 'mqttInitCommand', ...}], ...}] ...
 *
 * ```
 */
export const withScoutEntityChanges = (records: AuditRecord[]): AuditRecord[] => (
  records?.map(record => (
    record?.changes?.length > 0 ? record : {...record, changes: getScoutEntityChanges(record?.entity)}
  ))
);

/**
 * Parses the JSON values of the given scout audit changes into objects, so the
 * nested objects (e.g. `canRequest`) are displayed as a JSON tree, omitting
 * their `links` when empty.
 *
 * ```typescript
 *   import {parseScoutChanges} from './ScoutAuditUtils';
 *
 *   const changes = parseScoutChanges(auditRecord.changes);
 *   // [{fieldName: 'canRequest', oldValue: {status: 'PENDING', ...}, ...}] ...
 *
 * ```
 */
export const parseScoutChanges = (changes?: AuditChange[]): GenericObject[] => (
  changes?.map(({links, ...change}) => ({
    ...change,
    ...(links?.length > 0 && {links}),
    oldValue : getJSON(change?.oldValue),
    newValue : getJSON(change?.newValue)
  })) || []
);

/**
 * Defines how the scout audit log is requested and displayed: it requests the
 * audited entity, fills the records that omit `changes` from it and displays
 * the JSON values of the changes as a JSON tree.
 *
 * ```typescript
 *   import {SCOUT_AUDIT_OPTIONS} from './ScoutAuditUtils';
 *   ...
 *
 *   <AuditDialog item={auditLogItem}
 *     entity={Entity.vescout}
 *     transformOptions={SCOUT_AUDIT_OPTIONS} />
 *
 * ```
 */
export const SCOUT_AUDIT_OPTIONS: AuditTransformOptions = {
  includeEntity    : true,
  transformRecords : withScoutEntityChanges,
  transformChanges : parseScoutChanges
};
